import { FC, useEffect, useState } from 'react';
import { uniqBy } from 'lodash';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { CalendarDay, downloadFileFromTrpc } from 'further-ui/utils';
import { Row, Column } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import SearchField from 'further-ui/components/SearchField';
import Avatar from 'further-ui/components/Avatar';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import PageLoader from 'components/PageLoader';
import FullPageDrawer from 'components/new/FullPageDrawer';
import PageFilters, { Filter } from 'components/new/PageFilters';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { AccountingStatementExportType } from 'further-types/lp/general-ledger';
import { AccountingPeriodStatus } from 'further-types/lp/accounting-period';
import { SortOrder } from 'further-types/utils';
import api from 'lib/trpcClient';

const TableContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
});

const Checkbox = styled(MuiCheckbox)({
  padding: 0,
});

type Investor = {
  id: string;
  investorId: string;
  investorName: string;
};

type Props = {
  vehicleId: string;
  open: boolean;
  onClose: () => void;
};

const DownloadPcaps: FC<Props> = ({ vehicleId, open, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<Array<string>>(
    [],
  );
  const [format, setFormat] = useState<AccountingStatementExportType>(
    AccountingStatementExportType.Excel,
  );
  const parseFormat = (value: string) =>
    value === AccountingStatementExportType.Pdf
      ? AccountingStatementExportType.Pdf
      : AccountingStatementExportType.Excel;

  const {
    data: periodsData = [],
    isLoading: isPeriodsLoading,
    isError: isPeriodsError,
  } = api.accountingPeriod.listPeriods.useQuery(
    { vehicleId },
    { enabled: open },
  );
  const {
    data: commitmentsData,
    isLoading: isCommitmentsLoading,
    isError: isCommitmentsError,
  } = api.commitments.list.useQuery(
    {
      vehicleId,
      page: 1,
      rowsPerPage: 1000,
      order: SortOrder.Asc,
      orderBy: 'commitmentDate',
    },
    { enabled: open },
  );

  const [selectedDate, setSelectedDate] = useState<CalendarDay | null>(null);
  useEffect(() => {
    if (selectedDate) return;
    const initial = periodsData[0]?.periodEnd ?? null;
    if (!initial) return;
    setSelectedDate(initial);
  }, [periodsData, selectedDate]);

  const { mutateAsync: handleBulkDownload, isPending } =
    api.generalLedger.bulkDownloadPcap.useMutation(
      useHandleTrpcMutation()({ invalidations: [] }),
    );

  const periodOptions = periodsData.map((period) => {
    const isClosed = period.status === AccountingPeriodStatus.Approved;
    return {
      value: period.periodEnd.toString(),
      label: `${period.periodEnd.toReadable(true)}${
        isClosed ? ' (Closed)' : ''
      }`,
    };
  });

  const uniqueInvestors: Array<Investor> = uniqBy(
    commitmentsData?.results ?? [],
    (c) => c.investorId,
  ).map((c) => ({
    id: c.investorId,
    investorId: c.investorId,
    investorName: c.investorName,
  }));

  const filteredInvestors = uniqueInvestors.filter((investor) =>
    investor.investorName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const allVisibleInvestorsSelected =
    filteredInvestors.length > 0 &&
    filteredInvestors.every((investor) =>
      selectedInvestorIds.includes(investor.investorId),
    );

  const handleSelectDeselectAll = () => {
    setSelectedInvestorIds(
      allVisibleInvestorsSelected
        ? []
        : filteredInvestors.map((investor) => investor.investorId),
    );
  };

  const handleDownload = async () => {
    if (!selectedDate || selectedInvestorIds.length === 0) return;

    const data = await handleBulkDownload({
      vehicleId,
      investors: selectedInvestorIds.map((investorId) => ({ investorId })),
      endDate: selectedDate,
      format,
    });
    downloadFileFromTrpc(data);
  };

  const columns: Array<TableColumn<Investor>> = [
    {
      key: 'investor',
      render: ({ investorName }) => (
        <Row spacing="xs" centered>
          <Avatar color="random" text={investorName} size="lg" />
          <Typography variant="h6">{investorName}</Typography>
        </Row>
      ),
    },
    {
      key: 'actions',
      width: '50px',
      render: (investor) => (
        <Tooltip title="">
          <span>
            <Checkbox
              checked={selectedInvestorIds.includes(investor.investorId)}
              onChange={(e) => {
                setSelectedInvestorIds((prev) =>
                  e.target.checked
                    ? [...prev, investor.investorId]
                    : prev.filter((id) => id !== investor.investorId),
                );
              }}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <FullPageDrawer
      open={open}
      title="Download PCAPs"
      onClose={onClose}
      innerScroll
      actions={
        <Row spacing="sm" justify="space-between">
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            loading={isPending}
            onClick={handleDownload}
            disabled={selectedInvestorIds.length === 0 || !selectedDate}
          >
            Download
          </Button>
        </Row>
      }
    >
      <Column spacing="lg" fullWidth fullHeight>
        {isPeriodsLoading || isCommitmentsLoading ? (
          <PageLoader />
        ) : isPeriodsError || isCommitmentsError ? (
          <Column centered fullHeight justify="center">
            <Typography variant="body2">
              Could not load periods or investors. Please retry.
            </Typography>
          </Column>
        ) : (
          <>
            <Typography variant="body2">
              Select a period, format, and investors to download PCAPs.
            </Typography>
            <Column spacing="sm" fullWidth>
              {periodOptions.length > 0 && selectedDate && (
                <BasicSelect
                  value={selectedDate.toString()}
                  onChange={(value) => setSelectedDate(new CalendarDay(value))}
                  options={periodOptions}
                />
              )}
              <RadioGroup
                row
                value={format}
                onChange={(e) => setFormat(parseFormat(e.target.value))}
              >
                <FormControlLabel
                  value={AccountingStatementExportType.Excel}
                  control={<Radio />}
                  label={<Typography variant="body2">Spreadsheet</Typography>}
                />
                <FormControlLabel
                  value={AccountingStatementExportType.Pdf}
                  control={<Radio />}
                  label={<Typography variant="body2">PDF</Typography>}
                />
              </RadioGroup>
            </Column>
            <PageFilters
              justify="space-between"
              actions={
                filteredInvestors.length > 0
                  ? [
                      {
                        label: allVisibleInvestorsSelected
                          ? 'Deselect all'
                          : 'Select all',
                        onClick: handleSelectDeselectAll,
                      },
                    ]
                  : []
              }
            >
              <Filter size="md">
                <SearchField
                  fullWidth
                  placeholder="Search by investor"
                  value={searchQuery}
                  onSearch={setSearchQuery}
                  allowEmptyQuery
                  autoSearchOnDebounce
                  autoSearchOnBlur
                  debounceTime={1}
                />
              </Filter>
            </PageFilters>
            <TableContainer>
              <Table
                columns={columns}
                rows={filteredInvestors}
                emptyMessage="No investors found."
              />
            </TableContainer>
          </>
        )}
      </Column>
    </FullPageDrawer>
  );
};

export default DownloadPcaps;
